import { computed, inject, Injectable, signal } from "@angular/core";
import { MatDialog, MatDialogRef } from "@angular/material/dialog";
import { Observable, Subject, catchError, filter, finalize, map, of, take, takeUntil, timer } from "rxjs";
import { DbSchemeEnum } from "../enums/db-scheme.enum";
import { ProcedureNameEnum } from "../enums/procedure-name.enum";
import { NotificationDialogComponent } from "../components/notification-dialog/notification-dialog.component";
import { SharedApiService } from "./api/sharedApi.service";
import { LoginService } from "./login.service";
import { IExecuteProcedureResult, ResultCode } from "./model/pos.api.model";
import { StateService } from "./state.service";

export interface PosNotification {
  ID: number;
  DisplayType: number;
  Importance: number;
  InputType: number | null;
  Message: string;
  AddionalJsonData: string | null;
  Confirmed: boolean;
}

interface NotificationPage {
  Items: PosNotification[];
  Popups: PosNotification[];
  TotalCount: number;
  Page: number;
}

@Injectable({ providedIn: "root" })
export class PosNotificationsService {
  private readonly api = inject(SharedApiService);
  private readonly login = inject(LoginService);
  private readonly state = inject(StateService);
  private readonly dialog = inject(MatDialog);
  private readonly stop$ = new Subject<void>();

  private generation = 0;
  private loading = false;
  private queue: PosNotification[] = [];
  private seen = new Set<number>();
  private showing = false;
  private refreshPending = false;
  private pendingPopups = false;
  private waitingForDialogs = false;
  private activeDialog: MatDialogRef<NotificationDialogComponent> | null = null;

  readonly items = signal<PosNotification[]>([]);
  readonly count = signal(0);
  readonly page = signal(1);
  readonly pageSize = signal(0);
  readonly totalPages = computed(() =>
    this.pageSize() > 0 ? Math.ceil(this.count() / this.pageSize()) : 0
  );

  start(): void {
    this.stop();
    const minutes = this.state.configuration?.notificationRefreshMinutes;
    const size = this.state.configuration?.notificationPageSize;
    if (!this.login.user?.SelectedBusinessUnit || !minutes || minutes <= 0 || !size || size <= 0) {
      return;
    }

    this.pageSize.set(size);
    timer(0, minutes * 60000)
      .pipe(takeUntil(this.stop$))
      .subscribe(() => this.load(true));
  }

  stop(): void {
    this.generation++;
    const activeDialog = this.activeDialog;

    // Clear the queue before unsubscribing: afterClosed finalization calls next().
    this.queue = [];
    this.refreshPending = false;
    this.pendingPopups = false;
    this.stop$.next();
    activeDialog?.close();

    this.loading = false;
    this.items.set([]);
    this.count.set(0);
    this.page.set(1);
    this.seen.clear();
    this.waitingForDialogs = false;
    this.activeDialog = null;
  }

  changePage(page: number): void {
    if (page < 1 || (page - 1) * this.pageSize() >= this.count()) {
      return;
    }

    this.page.set(page);
    this.load(false);
  }

  private request<T>(name: ProcedureNameEnum, parameters: object): Observable<T | null> {
    return this.api.executeProcedureObservable<object, IExecuteProcedureResult<T>>({
      ProcedureName: name,
      DbScheme: DbSchemeEnum.Dert,
      Parameters: parameters,
    }).pipe(
      map(response => response?.result === ResultCode.OK ? response.data : null),
      catchError(error => {
        console.error("Notification request failed", error);
        return of(null);
      }),
    );
  }

  load(includePopups: boolean): void {
    if (this.showing || this.loading) {
      this.refreshPending = true;
      this.pendingPopups ||= includePopups;
      return;
    }

    const generation = this.generation;
    const requestedPage = this.page();
    this.loading = true;

    this.request<NotificationPage>(ProcedureNameEnum.GetPOSNotifications, {
      SelectedBusinessUnit: this.login.user?.SelectedBusinessUnit,
      Page: requestedPage,
      PageSize: this.pageSize(),
      IncludePopups: includePopups,
    }).pipe(
      takeUntil(this.stop$),
      finalize(() => {
        if (generation === this.generation) {
          this.loading = false;
          this.flushPendingRefresh();
        }
      }),
    ).subscribe(result => {
      if (!result || generation !== this.generation) {
        return;
      }

      // A request may have started just before a notification dialog opened.
      if (this.showing) {
        this.refreshPending = true;
        this.pendingPopups ||= includePopups;
        return;
      }

      // A page change during this request will be loaded after it finishes.
      if (requestedPage === this.page()) {
        this.items.set(result.Items);
        this.count.set(result.TotalCount);
        this.page.set(result.Page);
      }

      if (includePopups) {
        const ids = new Set((result.Popups ?? []).map(item => item.ID));
        this.queue = this.queue.filter(item => ids.has(item.ID));
        for (const item of result.Popups ?? []) {
          if (!this.seen.has(item.ID)) {
            this.seen.add(item.ID);
            this.queue.push(item);
          }
        }
        this.next();
      }
    });
  }

  open(item: PosNotification): void {
    if (this.showing) {
      return;
    }

    // A notification opened manually must not also open from the pending popup queue.
    this.seen.add(item.ID);
    this.queue = this.queue.filter(queued => queued.ID !== item.ID);
    this.showing = true;
    const generation = this.generation;
    this.activeDialog = this.dialog.open(NotificationDialogComponent, {
      data: item,
      disableClose: true,
      panelClass: "full-screen-dialog",
    });

    this.activeDialog.afterClosed().pipe(
      takeUntil(this.stop$),
      filter(value => value !== undefined),
      finalize(() => {
        this.activeDialog = null;
        this.showing = false;
        this.next();
        this.flushPendingRefresh();
      }),
    ).subscribe(value => {
      if (generation !== this.generation) {
        return;
      }

      this.request<{ Success: boolean }>(ProcedureNameEnum.SavePOSNotificationResponse, {
        SelectedBusinessUnit: this.login.user?.SelectedBusinessUnit,
        NotificationID: item.ID,
        Value: value,
      }).subscribe(result => {
        if (result?.Success && generation === this.generation) {
          this.load(false);
        }
      });
    });
  }

  private flushPendingRefresh(): void {
    if (this.showing || this.loading || !this.refreshPending) {
      return;
    }

    const includePopups = this.pendingPopups;
    this.refreshPending = false;
    this.pendingPopups = false;
    this.load(includePopups);
  }

  private next(): void {
    if (this.showing || !this.queue.length) {
      return;
    }
    if (this.dialog.openDialogs.length) {
      if (!this.waitingForDialogs) {
        this.waitingForDialogs = true;
        this.dialog.afterAllClosed.pipe(take(1), takeUntil(this.stop$)).subscribe(() => {
          this.waitingForDialogs = false;
          this.next();
        });
      }
      return;
    }

    const item = this.queue.shift();
    if (item) {
      this.open(item);
    }
  }
}
